import { getOrdersUrl, isSheetDbConfigured } from '../config/sheetdb'

export interface MenuItem {
  id: string
  name: string // Swedish (default)
  nameEn: string // English
  description?: string // Swedish
  descriptionEn?: string // English
  price: number
  categoryId: string
  isRoll?: boolean
  image?: string // product photo URL
}

export interface Category {
  id: string
  name: string // Swedish
  nameEn: string // English
  description?: string // Swedish
  descriptionEn?: string // English
}

export const CATEGORIES: Category[] = [
  {
    id: 'rolls',
    name: 'Frallor',
    nameEn: 'Rolls',
    description: '10 kr/st eller 5 för 45 kr',
    descriptionEn: '10 kr each or 5 for 45 kr',
  },
  { id: 'pastries', name: 'Fika', nameEn: 'Treats' },
  { id: 'filled-rolls', name: 'Bredda frallor', nameEn: 'Filled Rolls' },
  { id: 'bagels', name: 'Bagel fylld', nameEn: 'Filled Bagel' },
  { id: 'breakfast', name: 'Yoghurt & frukost', nameEn: 'Yoghurt & Breakfast' },
]

// The menu is managed here in code. SheetDB is used only to receive orders.
export const MENU_ITEMS: MenuItem[] = [
  {
    id: 'roll-four-grain',
    name: 'Fyrkornsfralla',
    nameEn: 'Four-Grain Roll',
    description:
      'Rågbaserad fralla med surdeg och fröblandning bestående av korngrits, linfrö, havrekli och rågflingor',
    descriptionEn:
      'Rye-based sourdough roll with a seed mix of barley grits, flaxseed, oat bran and rye flakes.',
    price: 10,
    categoryId: 'rolls',
    isRoll: true,
  },
  {
    id: 'roll-small-french',
    name: 'Småfranska',
    nameEn: 'Small French Roll',
    description: 'Vetefralla med vallmofrö',
    descriptionEn: 'Wheat roll topped with poppy seeds.',
    price: 10,
    categoryId: 'rolls',
    isRoll: true,
  },
  {
    id: 'roll-cheese',
    name: 'Ostfralla',
    nameEn: 'Cheese Roll',
    description: 'Vetefralla med smält ost',
    descriptionEn: 'Wheat roll with melted cheese.',
    price: 10,
    categoryId: 'rolls',
    isRoll: true,
  },
  {
    id: 'roll-ale',
    name: 'Alefralla',
    nameEn: 'Ale Roll',
    description:
      'Fiberrik vetefralla med Ale-brödbas och surdeg, innehåller rostat kornmaltmjöl, vetekli, surdeg, solroskärnor, linfrö och pumpakärnor',
    descriptionEn:
      'Fiber-rich wheat roll with an ale-bread base and sourdough, with roasted barley malt flour, wheat bran, sunflower seeds, flaxseed and pumpkin seeds.',
    price: 10,
    categoryId: 'rolls',
    isRoll: true,
  },
  {
    id: 'roll-sourdough',
    name: 'Surdegsfralla',
    nameEn: 'Sourdough Roll',
    description: 'Fralla på vetesurdeg, gjord på vetemjöl och vetesurdeg',
    descriptionEn:
      'Wheat sourdough roll made with wheat flour and wheat sourdough.',
    price: 10,
    categoryId: 'rolls',
    isRoll: true,
  },
  {
    id: 'pastry-cinnamon',
    name: 'Kanelbulle',
    nameEn: 'Cinnamon Bun',
    description: 'Vetebulle med kanelfyllning gjord på mandel',
    descriptionEn: 'Wheat bun with an almond-based cinnamon filling.',
    price: 38,
    categoryId: 'pastries',
    image:
      'https://cdn.magicpatterns.com/uploads/jgLuetJXXgBidY419NErZB/808e11ff-3e8c-4255-8f32-08f1c1c63452.jpg',
  },
  {
    id: 'pastry-vanilla-cardamom',
    name: 'Vanilj- & kardemummabulle',
    nameEn: 'Vanilla & Cardamom Bun',
    description: 'Vetebulle fylld med vaniljkräm och doppad i kardemummasocker',
    descriptionEn:
      'Wheat bun filled with vanilla cream and dipped in cardamom sugar.',
    price: 45,
    categoryId: 'pastries',
    image:
      'https://cdn.magicpatterns.com/uploads/1zp7Zfo9CrJ1G7m6ygqEEa/85c3c40d-1bc7-4755-b7dd-d75fdbb06fcb.jpg',
  },
  {
    id: 'pastry-croissant',
    name: 'Croissant',
    nameEn: 'Croissant',
    description: 'Kavlad croissant på svenskt smör',
    descriptionEn: 'Rolled croissant made with Swedish butter.',
    price: 38,
    categoryId: 'pastries',
  },
  {
    id: 'fika-cookie',
    name: 'Chocolate chip cookie',
    nameEn: 'Chocolate Chip Cookie',
    price: 45,
    categoryId: 'pastries',
    image:
      'https://cdn.magicpatterns.com/uploads/mMGcBdCjXvsY5zaoLeT144/93a54673-ffac-4654-a36a-b74bbe6802cf.jpg',
  },
  {
    id: 'fika-carrot-cake',
    name: 'Morotskaka',
    nameEn: 'Carrot Cake',
    price: 57,
    categoryId: 'pastries',
    image:
      'https://cdn.magicpatterns.com/uploads/amR5MKB5zagSQBGWBUENtr/74f7af45-1456-494a-8742-dad46d83336f.jpg',
  },
  {
    id: 'filled-cheese',
    name: 'Ost',
    nameEn: 'Cheese',
    price: 45,
    categoryId: 'filled-rolls',
    image:
      'https://cdn.magicpatterns.com/uploads/9PaWV5stHXsJCjqzwELHwZ/392701b5-fac8-4570-8d78-5adbc19a9b66.jpg',
  },
  {
    id: 'filled-cheese-ham',
    name: 'Ost & skinka',
    nameEn: 'Cheese & Ham',
    price: 45,
    categoryId: 'filled-rolls',
    image:
      'https://cdn.magicpatterns.com/uploads/umSnu7VA4YXHEHM7LuC1e5/c09c12b3-8284-4f36-8823-d0fb894d5a13.jpg',
  },
  {
    id: 'filled-salami-brie',
    name: 'Salami & brie',
    nameEn: 'Salami & Brie',
    price: 45,
    categoryId: 'filled-rolls',
    image:
      'https://cdn.magicpatterns.com/uploads/g44BLSZFhQGLcuiVTfCRFR/c400a5f8-2621-45fa-95ae-8f41aaa6041c.jpg',
  },
  {
    id: 'bagel-cream-cheese',
    name: 'Färskost & grönsaker',
    nameEn: 'Cream Cheese & Vegetables',
    description: 'Färskost (Philadelphia) med färska grönsaker',
    descriptionEn: 'Cream cheese (Philadelphia) with fresh vegetables.',
    price: 55,
    categoryId: 'bagels',
    image:
      'https://cdn.magicpatterns.com/uploads/ac1SzQZD9BDjGpf1KZJnEQ/47e9d7e4-79c5-4ea0-a91b-aef0972be6b0.jpg',
  },
  {
    id: 'bfast-yoghurt',
    name: 'Yoghurt med granola',
    nameEn: 'Yoghurt with Granola',
    price: 45,
    categoryId: 'breakfast',
  },
  {
    id: 'bfast-oats',
    name: 'Overnight chia',
    nameEn: 'Overnight Chia',
    description: 'Chiafrön med mango–passionsfruktkräm',
    descriptionEn: 'Chia seeds with a mango–passionfruit cream.',
    price: 45,
    categoryId: 'breakfast',
  },
]

// Mirrors the order sheet columns exactly:
// order_number | created_at | pickup_date | customer_name | phone | notes | items | category | item_count | total | status | TN | EC
// TN: Top Notch Bakery marks the order accepted. EC: Evedals Camping marks it picked up.
export interface OrderPayload {
  order_number: string
  created_at: string
  pickup_date: string
  customer_name: string
  phone: string
  notes: string
  items: string
  category: string
  item_count: number
  total: number
  status: string
  TN: string
  EC: string
}

export const submitOrder = async (order: OrderPayload): Promise<void> => {
  // No sheet connected — skip the network call so the flow still completes in preview.
  if (!isSheetDbConfigured()) {
    console.info('SheetDB not configured — order not sent to a sheet.', order)
    return
  }

  const response = await fetch(getOrdersUrl(), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: [order] }),
  })

  if (!response.ok) {
    // Surface the real reason (most common: a column in the payload does not
    // exist as a header in row 1 of the sheet, so SheetDB rejects the POST).
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      /* ignore */
    }
    console.error(
      `SheetDB order POST failed (HTTP ${response.status}). ` +
        `Make sure row 1 of your sheet has these exact headers: ` +
        `order_number, created_at, pickup_date, customer_name, phone, notes, ` +
        `items, category, item_count, total, status, TN, EC.`,
      detail,
    )
    throw new Error(`Failed to submit order (HTTP ${response.status})`)
  }
}

export const calculateLineTotal = (
  item: MenuItem,
  quantity: number,
): number => {
  if (item.isRoll) {
    const bundles = Math.floor(quantity / 5)
    const remainder = quantity % 5
    return bundles * 45 + remainder * 10
  }
  return item.price * quantity
}
